import {memoize} from 'lodash';
import {rgb} from 'polished';
import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {tryParseColor} from '../../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {lightTheme} from '../../../../../libs/shared/ui/src/theme/light/lightTheme';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {FormattingColorPickerDropdown} from './formattingColorPickerDropdown';

/*
 * Props.
 */

interface FormattingHighlightColorProps {
  highlightColor?: string;
  onHighlightColorSelect: (arg: string) => void;
  isDisabled?: boolean;
}

/*
 * Component.
 */

export const FormattingHighlightColor: FC<FormattingHighlightColorProps> = (props) => {
  const theme = useUserTheme();
  return (
    <DropdownBoxCoordinator
      value={renderValue()}
      size={VisualSizesEnum.SMALL}
      isDisabled={props.isDisabled}
      renderDropdown={(modalProps) => renderDropdown(modalProps, props, theme)}
      renderTooltip={() => <Trans id="formatting-texthighlight">Text highlight</Trans>}
      shouldPreventMouseDown
    />
  );
};

/*
 * Value.
 */

function renderValue() {
  return (
    <UserThemeConsumer>
      {(theme) => <Icon name="formattingHighlight" color={theme.greys.shade80} />}
    </UserThemeConsumer>
  );
}

/*
 * Dropdown.
 */

const pastelColors = [
  {color: '#dbf2ff'},
  {color: '#cef7f2'},
  {color: '#d2f4b7'},
  {color: '#fff1ae'},
  {color: '#fde2ce'},
  {color: '#ffd6d6'},
  {color: '#ffdbf5'},
  {color: '#e7d4fb'},
];

const neonColors = [
  {color: '#0057ff', lightForeground: true},
  {color: '#14fbdf'},
  {color: '#82fa22'},
  {color: '#fff627'},
  {color: '#ffa927'},
  {color: '#f00', lightForeground: true},
  {color: '#ff00b8', lightForeground: true},
  {color: '#9e00ff', lightForeground: true},
];

const findGreyColors = (theme: UserTheme) => [
  {color: undefined, borderColor: theme.greys.shade40, selectedBorderColor: theme.greys.shade70},
  {color: lightTheme.greys.shade30},
  {color: lightTheme.greys.shade40},
  {color: lightTheme.greys.shade50},
  {color: lightTheme.greys.shade60, lightForeground: true},
  {color: lightTheme.greys.shade70, lightForeground: true},
  {color: lightTheme.greys.shade80, lightForeground: true},
  {color: lightTheme.greys.shade90, lightForeground: true},
];

const findHighlightColors = memoize((theme: UserTheme) => [pastelColors, neonColors, findGreyColors(theme)]);

function renderDropdown(
  modalProps: PopoverTransferProps,
  props: FormattingHighlightColorProps,
  theme: UserTheme,
) {
  const onHighlightColorSelect = modalProps.wrap(props.onHighlightColorSelect);

  return (
    <FormattingColorPickerDropdown
      dropdownProps={modalProps}
      colors={findHighlightColors(theme)}
      selectedColor={colorToPalette(props.highlightColor)}
      onColorSelect={(color) => onHighlightColorSelect(colorFromPalette(color))}
    />
  );
}

function colorToPalette(color: string | undefined) {
  if (!color) {
    return undefined;
  }

  const parsedColor = tryParseColor(color);
  if (!parsedColor) {
    return undefined;
  }

  return rgb(parsedColor);
}

function colorFromPalette(color: string | undefined) {
  if (!color) {
    return '';
  }

  return color;
}
